import { Command } from 'cmdk'
import { motion, AnimatePresence } from 'framer-motion'
import * as React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { type City } from '#app/routes/api.cities'
import { cn } from '#app/utils/misc'

export interface CityComboboxProps {
	label?: string
	placeholder?: string
	error?: string
	disabled?: boolean
	onCitySelect: (city: City | null) => void
	defaultValue?: string
}

export function CityCombobox({
	label,
	placeholder = 'Search cities...',
	error,
	disabled,
	onCitySelect,
	defaultValue,
}: CityComboboxProps) {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState(defaultValue || '')
	const [cities, setCities] = useState<City[]>([])
	const [loading, setLoading] = useState(false)
	const [selectedCity, setSelectedCity] = useState<City | null>(null)

	// Debounced fetch
	useEffect(() => {
		if (!query || query.length < 2) {
			setCities([])
			return
		}

		const timer = setTimeout(async () => {
			setLoading(true)
			try {
				const response = await fetch(
					`/api/cities?q=${encodeURIComponent(query)}`,
				)
				const data = (await response.json()) as { cities: City[] }
				setCities(data.cities || [])
			} catch (error) {
				console.error('Error fetching cities:', error)
				setCities([])
			} finally {
				setLoading(false)
			}
		}, 300)

		return () => clearTimeout(timer)
	}, [query])

	const handleSelect = useCallback(
		(city: City) => {
			setSelectedCity(city)
			setQuery(`${city.name}, ${city.country}`)
			setOpen(false)
			onCitySelect(city)
		},
		[onCitySelect],
	)

	const handleInputChange = (value: string) => {
		setQuery(value)
		setSelectedCity(null)
		onCitySelect(null)
		if (value.length >= 2) {
			setOpen(true)
		} else {
			setOpen(false)
		}
	}

	return (
		<div className="w-full space-y-2">
			{label && (
				<label className="text-text-muted text-sm font-medium">{label}</label>
			)}
			<div className="relative">
				<Command
					shouldFilter={false}
					className={cn(
						'overflow-visible bg-transparent',
						disabled && 'opacity-50',
					)}
				>
					<div className="relative">
						<Command.Input
							value={query}
							onValueChange={handleInputChange}
							onFocus={() => query.length >= 2 && setOpen(true)}
							onBlur={() => setTimeout(() => setOpen(false), 200)}
							placeholder={placeholder}
							disabled={disabled}
							className={cn(
								'border-border bg-surface placeholder:text-text-muted focus:border-primary focus:ring-primary/20 flex h-12 w-full rounded-lg border px-4 py-3 text-sm text-white transition-all focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
								error &&
									'border-red-500 focus:border-red-500 focus:ring-red-500/20',
							)}
						/>
					</div>

					<AnimatePresence>
						{open && (query.length >= 2 || cities.length > 0) && (
							<motion.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								transition={{ duration: 0.15 }}
								className="absolute top-full z-50 mt-2 w-full"
							>
								<Command.List className="glass-effect border-border max-h-[300px] overflow-y-auto rounded-lg border p-2 shadow-xl">
									{loading && (
										<Command.Loading>
											<div className="text-text-muted px-4 py-3 text-sm">
												Searching cities...
											</div>
										</Command.Loading>
									)}

									{!loading && cities.length === 0 && query.length >= 2 && (
										<Command.Empty>
											<div className="text-text-muted px-4 py-3 text-sm">
												No cities found.
											</div>
										</Command.Empty>
									)}

									{!loading &&
										cities.map((city) => (
											<Command.Item
												key={`${city.name}-${city.country}-${city.lat}-${city.lng}`}
												value={`${city.name} ${city.country}`}
												onSelect={() => handleSelect(city)}
												className="hover:bg-surface-hover aria-selected:bg-surface-hover flex cursor-pointer items-center rounded-md px-4 py-3 text-sm text-white transition-colors"
											>
												<div className="flex flex-col">
													<span className="font-medium">{city.name}</span>
													<span className="text-text-muted text-xs">
														{city.country}
													</span>
												</div>
											</Command.Item>
										))}
								</Command.List>
							</motion.div>
						)}
					</AnimatePresence>
				</Command>
			</div>

			{error && (
				<motion.p
					initial={{ opacity: 0, y: -5 }}
					animate={{ opacity: 1, y: 0 }}
					className="text-xs text-red-500"
				>
					{error}
				</motion.p>
			)}
		</div>
	)
}
